import axios from 'axios';

import { API_URL } from '../config';

export interface FormatoImpresion {
  id: number;
  nombre: string;
  descripcion?: string;
  tipo?: string;
  encabezado_html?: string;
  pie_html?: string;
  logo_url?: string;
  fondo_url?: string;
  marca_agua_url?: string;
  banner_encabezado_url?: string;
  banner_pie_url?: string;
  configuracion_json?: string;
  design_json?: string;
}

export type FormatoResultadosOrigen = 'examen' | 'perfil' | 'categoria' | 'predeterminado' | 'sistema';

export interface FormatoResultadosResuelto {
  formato: FormatoImpresion;
  origen: FormatoResultadosOrigen;
  formato_id: number;
}

export interface OpcionesFormatoResultados {
  examenId?: number | null;
  categoriaId?: number | null;
  perfilId?: number | null;
}

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  };
};

/** Etiqueta legible del origen del formato resuelto. */
export function etiquetaOrigenFormato(origen: FormatoResultadosOrigen): string {
  switch (origen) {
    case 'examen':
      return 'Asignado a este examen';
    case 'perfil':
      return 'Asignado a este perfil';
    case 'categoria':
      return 'Heredado de la categoría';
    case 'predeterminado':
      return 'Formato predeterminado del laboratorio';
    case 'sistema':
      return 'Plantilla del sistema';
    default:
      return '';
  }
}

/**
 * Resuelve el formato de resultados con cascada: examen → categoría → predeterminado → sistema.
 */
export async function resolveFormatoResultados(
  options?: OpcionesFormatoResultados
): Promise<FormatoResultadosResuelto | null> {
  try {
    const params: Record<string, string | number> = { _t: Date.now() };
    if (options?.examenId != null && options.examenId > 0) params.examen_id = options.examenId;
    if (options?.categoriaId != null && options.categoriaId > 0) params.categoria_id = options.categoriaId;
    if (options?.perfilId != null && options.perfilId > 0) params.perfil_id = options.perfilId;
    const res = await axios.get(`${API_URL}/formatos/resolve/resultados`, {
      headers: authHeaders(),
      params,
    });
    return res.data;
  } catch {
    return null;
  }
}

/** Agrupa exámenes de una orden por formato resuelto (cascada por examen). */
export interface GrupoFormatoExamen {
  formato_id: number;
  formato: FormatoImpresion;
  origen: FormatoResultadosOrigen;
  examenes: any[];
}

export async function agruparExamenesPorFormatoResultados(examenes: any[]): Promise<GrupoFormatoExamen[]> {
  const map = new Map<number, GrupoFormatoExamen>();
  for (const ex of examenes) {
    const resolved = await resolveFormatoResultados({
      examenId: ex.id ?? ex.examen_id ?? null,
      categoriaId: ex.categoria_id ?? null,
      perfilId: ex.origen_perfil_id ?? ex.perfil_id ?? null,
    });
    if (!resolved?.formato) continue;
    const key = resolved.formato_id;
    if (!map.has(key)) {
      map.set(key, {
        formato_id: key,
        formato: resolved.formato,
        origen: resolved.origen,
        examenes: [],
      });
    }
    map.get(key)!.examenes.push(ex);
  }
  return Array.from(map.values());
}

/** Lista formatos de tipo resultados para asignar a exámenes. */
export async function listFormatosResultadosAsignables(): Promise<
  { id: number; nombre: string; descripcion?: string; es_predeterminado?: number }[]
> {
  try {
    const res = await axios.get(`${API_URL}/formatos/resultados/asignables`, {
      headers: authHeaders(),
    });
    return res.data || [];
  } catch {
    return [];
  }
}

/**
 * Obtiene el formato de impresión por tipo.
 * Para resultados use examenId y/o categoriaId (cascada en el servidor).
 */
export async function getFormatoPorTipo(
  tipo: string,
  categoriaIdOrOptions?: number | null | OpcionesFormatoResultados
): Promise<FormatoImpresion | null> {
  const opts: OpcionesFormatoResultados =
    typeof categoriaIdOrOptions === 'object' && categoriaIdOrOptions != null
      ? categoriaIdOrOptions
      : { categoriaId: categoriaIdOrOptions ?? undefined };

  if (tipo === 'resultados') {
    const resolved = await resolveFormatoResultados(opts);
    return resolved?.formato ?? null;
  }

  try {
    const params: Record<string, string | number> = { _t: Date.now() };
    if (opts.categoriaId != null && opts.categoriaId > 0) params.categoria_id = opts.categoriaId;
    const res = await axios.get(`${API_URL}/formatos/tipo/${tipo}`, {
      headers: authHeaders(),
      params,
    });
    return res.data;
  } catch {
    return null;
  }
}

/** Duplica un formato de resultados y lo asigna como variante exclusiva a una categoría. */
export async function duplicarFormatoParaCategoria(formatoId: number, categoriaId: number): Promise<FormatoImpresion | null> {
  const token = localStorage.getItem('token');
  try {
    const res = await axios.post(
      `${API_URL}/formatos/duplicar-para-categoria`,
      { formato_id: formatoId, categoria_id: categoriaId },
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
    return res.data;
  } catch {
    return null;
  }
}

/** Obtiene el formato de impresión por ID. */
export interface PlantillaBaseItem {
  id: string;
  nombre: string;
  descripcion: string;
  es_predeterminado_sugerido?: boolean;
}

export type PlantillaBaseResultados = PlantillaBaseItem;

export async function listPlantillasBaseResultados(): Promise<PlantillaBaseResultados[]> {
  try {
    const res = await axios.get(`${API_URL}/formatos/plantillas-base/resultados`, {
      headers: authHeaders(),
    });
    return res.data || [];
  } catch {
    return [];
  }
}

export async function listPlantillasBaseByTipo(tipo: string): Promise<PlantillaBaseItem[]> {
  try {
    const path =
      tipo === 'resultados'
        ? `${API_URL}/formatos/plantillas-base/resultados`
        : `${API_URL}/formatos/plantillas-base/${tipo}`;
    const res = await axios.get(path, { headers: authHeaders() });
    return res.data || [];
  } catch {
    return [];
  }
}

export async function crearFormatoDesdePlantillaBase(
  plantillaId: string,
  nombre?: string,
  tipo?: string
): Promise<FormatoImpresion | null> {
  try {
    const res = await axios.post(
      `${API_URL}/formatos/crear-desde-plantilla-base`,
      { plantilla_id: plantillaId, nombre: nombre || undefined, tipo: tipo || 'resultados' },
      { headers: authHeaders() }
    );
    return res.data;
  } catch {
    return null;
  }
}

export async function seedPlantillasBloquesByTipo(tipo: string): Promise<{
  message: string;
  inserted: number;
  skipped: number;
  total?: number;
} | null> {
  try {
    const path =
      tipo === 'resultados'
        ? `${API_URL}/formatos/seed-plantillas-bloques-resultados`
        : `${API_URL}/formatos/seed-plantillas-bloques/${tipo}`;
    const res = await axios.post(path, {}, { headers: authHeaders() });
    return res.data;
  } catch {
    return null;
  }
}

export async function seedPlantillasBloquesOrdenYFactura(): Promise<{
  message: string;
  inserted: number;
  skipped: number;
} | null> {
  try {
    const res = await axios.post(`${API_URL}/formatos/seed-plantillas-bloques-orden-factura`, {}, {
      headers: authHeaders(),
    });
    return res.data;
  } catch {
    return null;
  }
}

export async function duplicarFormato(formatoId: number, nombre?: string): Promise<FormatoImpresion | null> {
  try {
    const res = await axios.post(
      `${API_URL}/formatos/${formatoId}/duplicar`,
      { nombre: nombre || undefined },
      { headers: authHeaders() }
    );
    return res.data;
  } catch {
    return null;
  }
}

export async function seedPlantillasBloquesResultados(): Promise<{
  message: string;
  inserted: number;
  skipped: number;
} | null> {
  try {
    const res = await axios.post(`${API_URL}/formatos/seed-plantillas-bloques-resultados`, {}, {
      headers: authHeaders(),
    });
    return res.data;
  } catch {
    return null;
  }
}

export interface PredeterminadoPorTipo {
  tipo: string;
  formato_id: number;
}

export async function listPredeterminadosFormatos(): Promise<PredeterminadoPorTipo[]> {
  try {
    const res = await axios.get(`${API_URL}/formatos/predeterminados`, { headers: authHeaders() });
    return res.data || [];
  } catch {
    return [];
  }
}

export async function setFormatoPredeterminado(formatoId: number): Promise<FormatoImpresion | null> {
  try {
    const res = await axios.post(`${API_URL}/formatos/set-predeterminado/${formatoId}`, {}, { headers: authHeaders() });
    return res.data;
  } catch {
    return null;
  }
}

export async function migrateFormatoToBloques(formatoId: number): Promise<{
  message: string;
  migrated: boolean;
  formato?: FormatoImpresion;
} | null> {
  try {
    const res = await axios.post(`${API_URL}/formatos/${formatoId}/migrate-to-bloques`, {}, { headers: authHeaders() });
    return res.data;
  } catch {
    return null;
  }
}

export async function migrateAllCanvasToBloques(dryRun = false): Promise<{
  message: string;
  migrated: number;
  skipped: number;
  dryRun?: boolean;
  details?: { id: number; nombre: string; tipo: string }[];
} | null> {
  try {
    const res = await axios.post(
      `${API_URL}/formatos/migrate-canvas-to-bloques`,
      { dry_run: dryRun },
      { headers: authHeaders() }
    );
    return res.data;
  } catch {
    return null;
  }
}

export async function getFormatoPorId(id: number): Promise<FormatoImpresion | null> {
  const token = localStorage.getItem('token');
  try {
    const res = await axios.get(`${API_URL}/formatos/${id}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return res.data;
  } catch {
    return null;
  }
}

export type OrigenPlantillaWord = 'examen' | 'categoria' | 'general';

export interface PlantillaWordInfo {
  id: number;
  alcance: OrigenPlantillaWord;
  categoria_id?: number | null;
  examen_id?: number | null;
  origen?: OrigenPlantillaWord;
  archivo_nombre: string;
  nombre_original: string | null;
  categoria_nombre?: string | null;
  examen_nombre?: string | null;
  examen_codigo?: string | null;
  updated_at?: string;
}

export interface PlantillaWordResuelta {
  plantilla: PlantillaWordInfo;
  origen: OrigenPlantillaWord;
  plantilla_id: number;
}

export function etiquetaOrigenPlantillaWord(origen: OrigenPlantillaWord): string {
  switch (origen) {
    case 'examen':
      return 'Plantilla exclusiva del examen';
    case 'categoria':
      return 'Plantilla de la categoría';
    case 'general':
      return 'Plantilla general del laboratorio';
    default:
      return origen;
  }
}

/** Cascada examen → categoría → general. null si no hay ninguna plantilla Word activa. */
export async function resolvePlantillaWord(params: {
  examenId?: number | null;
  categoriaId?: number | null;
}): Promise<PlantillaWordResuelta | null> {
  try {
    const res = await axios.get<PlantillaWordResuelta>(`${API_URL}/formatos/resolve/plantilla-word`, {
      params: {
        examen_id: params.examenId ?? undefined,
        categoria_id: params.categoriaId ?? undefined,
      },
      headers: authHeaders(),
    });
    return res.data;
  } catch (e: unknown) {
    const status = (e as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw e;
  }
}

export async function listPlantillasWord(): Promise<PlantillaWordInfo[]> {
  try {
    const res = await axios.get<PlantillaWordInfo[]>(`${API_URL}/formatos/plantillas-word`, {
      headers: authHeaders(),
    });
    return res.data || [];
  } catch {
    return [];
  }
}
